import { type Dictionary } from '@ngrx/entity';

import { type CanvasElement } from '@blaze-runtime/engine-domain';
import { type StyleConfig } from '@blaze-shared/elements-domain';
import { isDefined } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import {
  type LayoutStyleReferencesInfo,
  STYLED_SLOT_SUPPORTED_ELEMENT_TYPES,
  type StyleSheet,
} from '@blaze-shared/theming-domain';

import {
  hasSlots,
  hasSlotStyleConfig,
  hasStylesConfig,
} from '../../canvas-elements/element-model/element-props-guards';

export function getStyleReferences(
  elements: CanvasElement[],
  styleSheets: StyleSheet[],
): LayoutStyleReferencesInfo[] | undefined {
  const styleElementsGroups: Dictionary<Set<CanvasElement>> = {};

  for (const el of elements) {
    if (
      STYLED_SLOT_SUPPORTED_ELEMENT_TYPES.includes(el.type) &&
      !hasStylesConfig(el) &&
      hasSlots(el) &&
      el.slots.ids
    ) {
      // Only the first slot with the assigned style should save reference for an element.
      // Example: Container with multiple columns and styles assigned to them.
      let savedRefForElement = false;
      for (const slotId of el.slots.ids) {
        const slot = el.slots[slotId];
        if (savedRefForElement || !hasSlotStyleConfig(slot)) {
          continue;
        }
        updateStyleElementsGroupMutably(styleElementsGroups, slot.props.styleConfig, el);
        savedRefForElement = true;
      }
    }

    if (!hasStylesConfig(el)) {
      continue;
    }
    updateStyleElementsGroupMutably(styleElementsGroups, el.props.styleConfig, el);
  }

  return Object.entries(styleElementsGroups)
    .filter(([styleIdentifier]) => !!styleIdentifier)
    .map(([styleIdentifier, setOfElements]) => {
      const elements = Array.from(setOfElements || []);
      const [firstElement] = elements;
      const firstStyleConfig = findFirstStyleConfig(firstElement, styleIdentifier);

      if (!firstStyleConfig) {
        return undefined;
      }

      const type = firstElement.type;
      const { styleName, styleSheetId } = firstStyleConfig;
      const style = fromStyles.findMatchingStyleSheet({
        id: styleSheetId,
        name: styleName,
        elementType: type,
        sheets: styleSheets,
      });

      if (!style) {
        return undefined;
      }
      const referencedByName = !!style.themeId;

      if (referencedByName) {
        return {
          styleName: style.name,
          elementType: style.elementType,
          elementIds: elements.map((el) => el.id),
        };
      }
      return {
        styleId: style.id,
        elementIds: elements.map((el) => el.id),
      };
    })
    .filter(isDefined);
}

function updateStyleElementsGroupMutably(
  groups: Dictionary<Set<CanvasElement>>,
  styleConfig: StyleConfig | undefined,
  element: CanvasElement,
): void {
  const { styleName, styleSheetId } = styleConfig || {};
  if (!styleName && !styleSheetId) {
    return;
  }

  if (styleSheetId) {
    const set = groups[styleSheetId] ?? new Set();
    set.add(element);
    groups[styleSheetId] = set;
  } else {
    const key = `${styleName}_${element.type}`;
    const set = groups[key] ?? new Set();
    set.add(element);
    groups[key] = set;
  }
}

function findFirstStyleConfig(
  element: CanvasElement,
  expectedStyleIdentifier: string,
): StyleConfig | undefined {
  if (!hasStylesConfig(element) && !STYLED_SLOT_SUPPORTED_ELEMENT_TYPES.includes(element.type)) {
    return undefined;
  }

  if (hasStylesConfig(element)) {
    return element.props.styleConfig;
  }

  if (!hasSlots(element)) {
    return undefined;
  }

  for (const slotId of element.slots.ids) {
    const slot = element.slots[slotId];
    if (!hasSlotStyleConfig(slot)) {
      continue;
    }

    const { styleName, styleSheetId } = slot?.props?.styleConfig || {};
    if (
      (styleName && expectedStyleIdentifier.includes(styleName)) ||
      (styleSheetId && expectedStyleIdentifier.includes(styleSheetId))
    ) {
      return slot.props.styleConfig;
    }
  }

  return undefined;
}
